/**
 * XRM fleet Excel import wizard (Settings → Aircraft).
 */

export function openXrmImportWizard({ el, fetchJson, apiUrl, cleanText, isAdminSession, onComplete }) {
  if (!isAdminSession()) {
    window.alert("Admin only.");
    return;
  }

  const state = {
    sessionId: null,
    stats: null,
    current: null,
    loading: false,
    regLookupAttempted: new Set(),
    regTrackingByValue: "",
    regTrackingMessage: "",
  };

  const isValidRegistration = (reg) => {
    const r = cleanText(reg).toUpperCase();
    return Boolean(r && r.length >= 2 && !/^XRM-/i.test(r));
  };

  const summaryEl = el("div", { class: "grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" }, []);
  const dropZone = el("div", {
    class:
      "border-2 border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-600 hover:border-dae hover:bg-dae/5 cursor-pointer",
    text: "Drop XRM Excel here or click to select (.xlsx)",
  });
  const fileInput = el("input", { type: "file", accept: ".xlsx,.xls", class: "hidden" });
  const reviewHost = el("div", { class: "space-y-4" }, []);
  const statusLine = el("p", { class: "text-sm text-gray-600", text: "" });
  const reportBtn = el("button", {
    type: "button",
    class:
      "hidden inline-flex items-center px-3 py-2 text-sm font-medium text-dae bg-white border border-dae/30 rounded-lg hover:bg-red-50",
    text: "Download import report",
  });
  const reportCsvBtn = el("button", {
    type: "button",
    class:
      "hidden inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "CSV",
  });

  const refreshFleetTables = async () => {
    if (typeof onComplete === "function") {
      try {
        await onComplete();
      } catch {
        /* ignore */
      }
    }
  };

  const downloadImportReport = (format = "html") => {
    if (!state.sessionId) return;
    const q = format === "csv" ? "?format=csv" : "?format=html";
    window.location.href = apiUrl(`/api/admin/xrm-import/session/${encodeURIComponent(state.sessionId)}/report${q}`);
  };
  reportBtn.onclick = () => downloadImportReport("html");
  reportCsvBtn.onclick = () => downloadImportReport("csv");

  const profileAfterClass = (status) => {
    if (status === "changed" || status === "added") return "px-3 py-2 bg-green-50 text-green-800 font-medium";
    if (status === "skipped") return "px-3 py-2 bg-blue-50 text-blue-800 font-medium";
    if (status === "removed") return "px-3 py-2 bg-red-50 text-red-800 font-medium";
    return "px-3 py-2 text-gray-600";
  };

  const renderProfileTable = (rows, { caption } = {}) => {
    const list = Array.isArray(rows) ? rows : [];
    return el("div", { class: "space-y-2" }, [
      caption
        ? el("p", { class: "text-xs text-gray-500", text: caption })
        : null,
      el("div", { class: "flex flex-wrap gap-2 text-[11px]" }, [
        el("span", { class: "px-2 py-0.5 rounded bg-green-50 text-green-800", text: "Green = will change / changed" }),
        el("span", { class: "px-2 py-0.5 rounded bg-blue-50 text-blue-800", text: "Blue = skipped" }),
        el("span", { class: "px-2 py-0.5 rounded bg-red-50 text-red-800", text: "Red = Active deactivated" }),
        el("span", { class: "px-2 py-0.5 rounded bg-gray-100 text-gray-600", text: "Gray = unchanged" }),
      ]),
      el("div", { class: "overflow-x-auto border border-gray-200 rounded-lg" }, [
        el("table", { class: "w-full text-sm" }, [
          el("thead", { class: "bg-gray-50 text-xs uppercase text-gray-600" }, [
            el("tr", {}, [
              el("th", { class: "px-3 py-2 text-left", text: "Field" }),
              el("th", { class: "px-3 py-2 text-left", text: "Before (in AC Tracker)" }),
              el("th", { class: "px-3 py-2 text-left", text: "After (from XRM)" }),
            ]),
          ]),
          el(
            "tbody",
            {},
            list.map((f) =>
              el("tr", { class: "border-t" }, [
                el("td", { class: "px-3 py-2 font-medium text-gray-900", text: f.label || f.field }),
                el("td", { class: "px-3 py-2 text-gray-500", text: f.before ?? f.oldValue ?? "—" }),
                el("td", {
                  class: profileAfterClass(f.status || (f.oldValue !== f.newValue ? "changed" : "unchanged")),
                  text: f.after ?? f.newValue ?? "—",
                }),
              ])
            )
          ),
        ]),
      ]),
    ]);
  };
  let regVerifyTimer = null;
  const verifyRegistrationLive = (reg) => {
    const trackingEl = document.getElementById("xrm-reg-tracking-status");
    if (!trackingEl) return;
    if (!isValidRegistration(reg)) {
      trackingEl.textContent = reg ? "Enter a valid registration (required before create)." : "";
      trackingEl.className = "mt-2 text-sm text-gray-500";
      return;
    }
    if (state.regTrackingByValue === reg && state.regTrackingMessage) {
      trackingEl.textContent = state.regTrackingMessage;
      trackingEl.className = state.regTrackingMessage.includes("found")
        ? "mt-2 text-sm text-green-700 font-medium"
        : "mt-2 text-sm text-amber-700";
      return;
    }
    trackingEl.textContent = "Checking FlightAware / FR24…";
    trackingEl.className = "mt-2 text-sm text-gray-600";
    fetchJson(apiUrl("/api/admin/xrm-import/verify-registration"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ registration: reg }),
    })
      .then((r) => {
        state.regTrackingByValue = reg;
        state.regTrackingMessage = cleanText(r?.message) || (r?.found ? "Tracking data found." : "No tracking data found.");
        trackingEl.textContent = state.regTrackingMessage;
        trackingEl.className = r?.found
          ? "mt-2 text-sm text-green-700 font-medium"
          : "mt-2 text-sm text-amber-700";
      })
      .catch((e) => {
        trackingEl.textContent = cleanText(e?.message) || "Tracking check failed.";
        trackingEl.className = "mt-2 text-sm text-red-700";
      });
  };

  const scheduleRegVerify = (reg) => {
    if (regVerifyTimer) clearTimeout(regVerifyTimer);
    regVerifyTimer = setTimeout(() => verifyRegistrationLive(reg), 400);
  };

  const renderSummary = (data) => {
    const inactiveN = data?.notInFileInactiveCount ?? data?.stats?.notInFileInactiveCount;
    const items = [
      ["Source records", data?.sourceCount ?? "—"],
      ["Changes pending", data?.changeCount ?? data?.stats?.changeCount ?? "—"],
      ["New aircraft", data?.newCount ?? data?.stats?.newCount ?? "—"],
      ["Removed from file", data?.removedCount ?? data?.stats?.removedCount ?? "—"],
    ];
    if (inactiveN != null && Number(inactiveN) > 0) {
      items.push(["Already inactive (not in file)", inactiveN]);
    }
    summaryEl.replaceChildren(
      ...items.map(([label, val]) =>
        el("div", { class: "bg-gray-50 border border-gray-200 rounded-lg p-3" }, [
          el("div", { class: "text-xs text-gray-500", text: label }),
          el("div", { class: "text-xl font-semibold text-gray-900", text: String(val) }),
        ])
      ),
      Number(inactiveN) > 0
        ? el("p", {
            class: "sm:col-span-3 text-xs text-gray-600 -mt-1",
            text: "Aircraft already marked inactive are not offered again for deactivation.",
          })
        : null
    );
  };

  const loadSessionItem = async () => {
    if (!state.sessionId) {
      reviewHost.replaceChildren([]);
      reportBtn.classList.add("hidden");
      reportCsvBtn.classList.add("hidden");
      return;
    }
    reportBtn.classList.remove("hidden");
    reportCsvBtn.classList.remove("hidden");

    const resp = await fetchJson(apiUrl(`/api/admin/xrm-import/session/${state.sessionId}`));
    const pending = resp?.session?.payload?.pending || {};
    const stats = resp?.session?.payload?.stats || state.stats;
    state.stats = stats;
    if (stats) renderSummary({ ...state.stats, sourceCount: resp?.session?.payload?.sourceCount });
    const next = pending.changes?.[0] || pending.newRecords?.[0] || pending.removed?.[0] || null;
    state.current = next;

    if (!next) {
      reviewHost.replaceChildren(
        el("div", { class: "rounded-lg border border-green-200 bg-green-50 p-4 text-green-900 text-sm space-y-3" }, [
          el("p", {
            text: "Import review complete. Accepted changes were written to the database immediately as you confirmed each item.",
          }),
          el("div", { class: "flex flex-wrap gap-2" }, [
            el("button", {
              type: "button",
              class:
                "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
              text: "Download import report",
              onclick: () => downloadImportReport("html"),
            }),
            el("button", {
              type: "button",
              class:
                "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg",
              text: "CSV",
              onclick: () => downloadImportReport("csv"),
            }),
          ]),
        ])
      );
      statusLine.textContent = "Done — download the report for the full before/after profile of every aircraft.";
      await refreshFleetTables();
      return;
    }

    const kind = next.id?.startsWith("new:")
      ? "new"
      : next.id?.startsWith("rm:")
        ? "removed"
        : "change";

    const identLabel = next.fleetIdent || next.fleet_ident || `MSN ${next.msn}`;
    const header = el("div", { class: "flex items-center justify-between gap-2" }, [
      el("h3", {
        class: "text-base font-semibold text-gray-900",
        text:
          kind === "new"
            ? `New aircraft — ${identLabel}`
            : kind === "removed"
              ? `Removed from XRM — ${identLabel}`
              : `Change — ${identLabel} (${next.registration || "—"})`,
      }),
      el("span", {
        class: "text-xs text-gray-500",
        text: `${(pending.changes?.length || 0) + (pending.newRecords?.length || 0) + (pending.removed?.length || 0)} left`,
      }),
    ]);

    const bodyParts = [header];

    if (kind === "change") {
      const rows =
        Array.isArray(next.profileRows) && next.profileRows.length
          ? next.profileRows
          : (next.fieldChanges || []).map((f) => ({
              label: f.label,
              before: f.oldValue,
              after: f.newValue,
              status: "changed",
            }));
      bodyParts.push(
        renderProfileTable(rows, {
          caption:
            "Full aircraft profile — Accept writes every green field from XRM. Skip leaves the database unchanged.",
        })
      );
    }

    if (kind === "new") {
      const incoming = next.incoming || {};
      const rows =
        Array.isArray(next.profileRows) && next.profileRows.length
          ? next.profileRows
          : [
              { label: "Operator", before: "—", after: incoming.operator || "—", status: "added" },
              { label: "FM", before: "—", after: incoming.owner || "—", status: "added" },
              { label: "Operator country", before: "—", after: incoming.operator_country || "—", status: "added" },
              { label: "Operator region", before: "—", after: incoming.operator_region || "—", status: "added" },
              { label: "Aircraft series", before: "—", after: incoming.aircraft_series || "—", status: "added" },
              { label: "Aircraft model", before: "—", after: incoming.aircraft_type || "—", status: "added" },
              { label: "Engine series", before: "—", after: incoming.engine_series || "—", status: "added" },
              { label: "Engine model", before: "—", after: incoming.engine_model || "—", status: "added" },
              { label: "Active", before: "—", after: "Yes", status: "added" },
            ];
      bodyParts.push(
        el("p", {
          class: "text-xs text-gray-600",
          text: `Fleet ident: ${next.fleetIdent || next.fleet_ident || "—"} · MSN ${incoming.msn || next.msn || "—"}`,
        }),
        renderProfileTable(rows, {
          caption: "New aircraft — all green fields will be created. Registration is required below.",
        }),
        el("p", {
          class: "text-xs text-red-700 font-medium mt-2",
          text: "Registration is required — aircraft cannot be created without a valid tail number.",
        })
      );

      if (!next.registrationLookupDone && !state.regLookupAttempted.has(next.id)) {
        state.regLookupAttempted.add(next.id);
        bodyParts.push(
          el("div", { class: "mt-3 text-sm text-gray-600", text: "Looking up registration…" })
        );
        reviewHost.replaceChildren(
          el("div", { class: "border border-gray-200 rounded-xl p-4 bg-white" }, bodyParts)
        );
        fetchJson(apiUrl("/api/admin/xrm-import/suggest-registration"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: state.sessionId, itemId: next.id }),
        })
          .then(() => loadSessionItem())
          .catch(() => loadSessionItem());
        return;
      }

      const suggested = cleanText(next.suggestedRegistration);
      if (suggested) {
        bodyParts.push(
          el("div", {
            class: "mt-3 text-sm font-bold text-red-600",
            text: `SUGGESTED registration: ${suggested}`,
          })
        );
      } else {
        bodyParts.push(
          el("div", {
            class: "mt-3 text-sm text-gray-600",
            text: "No registration found automatically — enter manually.",
          })
        );
      }
      const initialReg = cleanText(next.confirmedRegistration || next.suggestedRegistration || "");
      const regInput = el("input", {
        id: "xrm-reg-input",
        class:
          "mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono uppercase",
        placeholder: "Registration (required)",
        value: initialReg,
        oninput: (ev) => scheduleRegVerify(cleanText(ev.target?.value)),
      });
      const trackingStatus = el("div", {
        id: "xrm-reg-tracking-status",
        class: "mt-2 text-sm text-gray-500",
        text: "",
      });
      bodyParts.push(el("div", { class: "mt-2" }, [regInput, trackingStatus]));
      if (initialReg) scheduleRegVerify(initialReg);
    }

    if (kind === "removed") {
      const rows =
        Array.isArray(next.profileRows) && next.profileRows.length
          ? next.profileRows
          : [
              { label: "Registration", before: next.registration || "—", after: next.registration || "—", status: "unchanged" },
              { label: "Operator", before: next.operator || "—", after: next.operator || "—", status: "unchanged" },
              { label: "FM", before: next.owner || "—", after: next.owner || "—", status: "unchanged" },
              { label: "Active", before: "Yes", after: "No", status: "removed" },
            ];
      bodyParts.push(
        el("p", {
          class: "text-sm text-gray-800 font-medium",
          text: `MSN ${next.msn} is in AC Tracker but missing from this XRM file.`,
        }),
        renderProfileTable(rows, {
          caption: "Confirm to deactivate (Active → No). The row is not deleted from the database.",
        })
      );
    }

    const applyItem = async ({ action, registration }) => {
      if (state.loading) return;
      state.loading = true;
      try {
        const result = await fetchJson(apiUrl("/api/admin/xrm-import/apply"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: state.sessionId,
            itemId: next.id,
            action,
            registration,
          }),
        });
        if (result?.stale) {
          statusLine.textContent = cleanText(result.message) || "List refreshed.";
        }
        await refreshFleetTables();
        if (cleanText(result?.trackingNote)) {
          statusLine.textContent = cleanText(result.trackingNote);
        }
        await loadSessionItem();
      } finally {
        state.loading = false;
      }
    };

    const actions = el("div", { class: "flex flex-wrap gap-2 pt-2" }, [
      el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
        text: kind === "change" ? "Accept change" : kind === "new" ? "Accept & create" : "Deactivate aircraft",
          onclick: async () => {
            if (state.loading) return;
            const action =
              kind === "change" ? "accept_change" : kind === "new" ? "accept_new" : "accept_removed";
            const registration =
              kind === "new" ? cleanText(document.getElementById("xrm-reg-input")?.value) : undefined;
            if (kind === "new" && !isValidRegistration(registration)) {
              window.alert("Enter a valid registration before creating aircraft. XRM placeholders are not allowed.");
              return;
            }
            try {
              await applyItem({ action, registration });
            } catch (e) {
              window.alert(cleanText(e?.message) || String(e));
              await loadSessionItem().catch(() => {});
            }
          },
      }),
      el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Skip (no change)",
          onclick: async () => {
            if (state.loading) return;
            try {
              await applyItem({ action: "reject" });
            } catch (e) {
              window.alert(cleanText(e?.message) || String(e));
              await loadSessionItem().catch(() => {});
            }
          },
      }),
    ]);

    if (kind === "change" && (pending.changes?.length || 0) > 1) {
      actions.appendChild(
        el("button", {
          type: "button",
          class:
            "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900",
          text: `Approve all ${pending.changes.length} changes`,
          onclick: async () => {
            if (!window.confirm(`Apply all ${pending.changes.length} pending field changes to the database now?`)) return;
            try {
              await fetchJson(apiUrl("/api/admin/xrm-import/apply"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId: state.sessionId, action: "accept_changes", approveAll: true }),
              });
              await refreshFleetTables();
              await loadSessionItem();
            } catch (e) {
              window.alert(cleanText(e?.message) || String(e));
            }
          },
        })
      );
    }

    bodyParts.push(actions);
    reviewHost.replaceChildren(el("div", { class: "border border-gray-200 rounded-xl p-4 bg-white" }, bodyParts));
  };

  const uploadFile = async (file) => {
    if (!file) return;
    statusLine.textContent = "Parsing XRM file…";
    const buf = await file.arrayBuffer();
    const resp = await fetch(apiUrl(`/api/admin/xrm-import/parse?filename=${encodeURIComponent(file.name)}`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      body: buf,
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data?.ok) {
      throw new Error(cleanText(data?.error) || `Upload failed (${resp.status})`);
    }
    state.sessionId = data.sessionId;
    state.stats = data;
    renderSummary(data);
    statusLine.textContent = `Parsed row ${data.headerRowIndex || "?"} headers. Review items one by one — each accept writes to the DB immediately.`;
    if (data.warnings?.length) {
      statusLine.textContent += ` Warnings: ${data.warnings.map((w) => w.message || w).join("; ")}`;
    }
    await loadSessionItem();
  };

  dropZone.onclick = () => fileInput.click();
  dropZone.ondragover = (e) => {
    e.preventDefault();
    dropZone.classList.add("border-dae", "bg-dae/5");
  };
  dropZone.ondragleave = () => dropZone.classList.remove("border-dae", "bg-dae/5");
  dropZone.ondrop = (e) => {
    e.preventDefault();
    dropZone.classList.remove("border-dae", "bg-dae/5");
    const file = e.dataTransfer?.files?.[0];
    uploadFile(file).catch((err) => {
      statusLine.textContent = cleanText(err?.message) || String(err);
    });
  };
  fileInput.onchange = () => {
    const file = fileInput.files?.[0];
    uploadFile(file).catch((err) => {
      statusLine.textContent = cleanText(err?.message) || String(err);
    });
  };

  const body = el("div", {}, [
    el("div", { class: "flex flex-wrap items-center justify-between gap-2 mb-2" }, [
      el("div", { class: "flex flex-wrap gap-2" }, [reportBtn, reportCsvBtn]),
    ]),
    summaryEl,
    dropZone,
    fileInput,
    statusLine,
    reviewHost,
  ]);
  const closeBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Close",
  });

  const overlay = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    onclick: (e) => {
      if (e.target === overlay) overlay.remove();
    },
  }, [
    el("div", { class: "bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" }, [
      el("h2", { class: "text-xl font-bold text-gray-900 mb-1", text: "XRM fleet import" }),
      el("p", {
        class: "text-sm text-gray-600 mb-4",
        text: "Compare the XRM Excel against the fleet database. Confirm each new, changed, or removed aircraft — changes apply immediately. Removed aircraft are deactivated (inactive), not deleted. Full history: Settings → XRM imports.",
      }),
      body,
      el("div", { class: "mt-4 flex justify-end" }, [closeBtn]),
    ]),
  ]);
  closeBtn.onclick = () => overlay.remove();
  document.body.appendChild(overlay);
}

/** Settings → XRM imports — permanent session history from the database. */
export function renderXrmImportSettingsPanel({ el, fetchJson, apiUrl, cleanText, formatDisplayTimestamp, isAdminSession }) {
  if (!isAdminSession()) {
    return el("div", { class: "text-sm text-gray-600", text: "Admin only." });
  }

  const host = el("div", { class: "space-y-4" });
  const meta = el("p", { class: "text-sm text-gray-600", text: "Loading import history…" });
  const tableHost = el("div", {});

  const renderRows = (sessions) => {
    if (!sessions?.length) {
      tableHost.replaceChildren(el("p", { class: "text-sm text-gray-500", text: "No XRM imports recorded yet." }));
      return;
    }
    const rows = sessions.map((s) => {
      const stats = s.stats && typeof s.stats === "object" ? s.stats : {};
      const when = s.completed_at || s.created_at;
      const summary = [
        stats.changeCount != null ? `${stats.changeCount} changes` : null,
        stats.newCount != null ? `${stats.newCount} new` : null,
        stats.removedCount != null ? `${stats.removedCount} removed` : null,
      ].filter(Boolean).join(" · ");
      return el("tr", { class: "border-t border-gray-100 hover:bg-gray-50" }, [
        el("td", { class: "px-3 py-2 text-sm whitespace-nowrap", text: when ? formatDisplayTimestamp(when) : "—" }),
        el("td", { class: "px-3 py-2 text-sm", text: cleanText(s.filename) || "—" }),
        el("td", { class: "px-3 py-2 text-sm", text: cleanText(s.uploaded_by) || "—" }),
        el("td", { class: "px-3 py-2 text-sm text-gray-600", text: summary || "—" }),
        el("td", { class: "px-3 py-2 text-sm" }, [
          s.completed_at
            ? el("a", {
                class: "text-dae hover:underline",
                href: apiUrl(`/api/admin/xrm-import/session/${encodeURIComponent(s.id)}/report`),
                text: "CSV log",
              })
            : el("span", { class: "text-amber-700", text: "In progress" }),
        ]),
      ]);
    });
    tableHost.replaceChildren(
      el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl" }, [
        el("table", { class: "min-w-full text-left text-sm" }, [
          el("thead", { class: "bg-dae text-white text-xs uppercase" }, [
            el("tr", {}, ["When", "File", "By", "Summary", "Log"].map((h) => el("th", { class: "px-3 py-2", text: h }))),
          ]),
          el("tbody", {}, rows),
        ]),
      ])
    );
  };

  const load = async () => {
    try {
      const [hist, last] = await Promise.all([
        fetchJson(apiUrl("/api/admin/xrm-import/history?limit=25")),
        fetchJson(apiUrl("/api/admin/xrm-import/last")),
      ]);
      const lastRow = last?.last;
      if (lastRow?.completed_at) {
        meta.textContent = `Last completed import: ${formatDisplayTimestamp(lastRow.completed_at)} · ${cleanText(lastRow.filename) || "—"} · by ${cleanText(lastRow.uploaded_by) || "admin"}`;
      } else {
        meta.textContent = "No completed XRM import on record yet.";
      }
      renderRows(hist?.sessions || []);
    } catch (e) {
      meta.textContent = cleanText(e?.message) || "Failed to load import history.";
      tableHost.replaceChildren();
    }
  };

  host.replaceChildren(
    el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4" }, [
      el("h2", { class: "text-lg font-semibold text-gray-900", text: "XRM import history" }),
      el("p", {
        class: "text-sm text-gray-600",
        text: "Permanent log of fleet Excel imports. Each completed session is also recorded in Activity. Aircraft removed from the file are deactivated (inactive), not deleted.",
      }),
      meta,
      tableHost,
      el("button", {
        type: "button",
        class: "text-sm text-dae hover:underline",
        text: "Refresh",
        onclick: () => load(),
      }),
    ])
  );
  load();
  return host;
}
